import { Checkbox, FormControlLabel, Grid2 } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { ClientCategoryEnum } from "../../../_common/enum/clientCategory.enum";
import { DataProcessingCategoryEnum } from "../../../_common/enum/dataProcessingCategory.enum";
import EnumSelect from "../../../_components/autocomplete/enum.select";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { getFormatedDate } from "../../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { clientService } from "../../../_services/client.service";
import { DataTypeEnum } from "../../../_common/enum/dataType.enum";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import { ClientPaymentTermsEnum } from "../../../_common/enum/clientPaymentTerms.enum";

export function ClientInfoManage(params: { onSave(): void }) {

    const resetData = () => ({});

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);

    useEffect(() => {
        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return clientService
            .get(id)
            .then(
                (json) => {
                    console.log(json);
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        return clientService
            .submit(data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        setIsLoading(true);
        return clientService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    const handleAddressChange = (event: any) => {
        setData((_data: any) => ({
            ..._data, address:
            {
                ..._data.address, [event.target.name]: event.target.value
            }
        }));
    };

    return (
        <ValidatorForm
            onSubmit={submit}
            noValidate>

            <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="name"
                        name="name"
                        label="Name"
                        required
                        fullWidth
                        validators={['required', 'minStringLength:1']}
                        errorMessages={['required field', "Field cannot be empty"]}
                        value={data.name}
                        onChange={handleChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <EnumSelect
                        key="clientCategory"
                        enumObject={ClientCategoryEnum}
                        label="Select Category"
                        name="clientCategoryId"
                        value={data.clientCategoryId}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <EnumSelect
                        key="clientPaymentTermsId"
                        enumObject={ClientPaymentTermsEnum}
                        label="Select Payment Terms"
                        name="clientPaymentTermsId"
                        value={data.clientPaymentTermsId}
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <EnumSelect
                        key="dataProcessingCategoryId"
                        enumObject={DataProcessingCategoryEnum}
                        label="Select Data Processing Category"
                        name="dataProcessingCategoryId"
                        value={data.dataProcessingCategoryId}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="phone"
                        name="phone"
                        label="Phone"
                        fullWidth
                        validators={['matchRegexp:^\\+?[0-9]{7,15}$']}
                        errorMessages={['Enter a valid phone number (7-15 digits, optional +)']}
                        value={data.phone}
                        onChange={handleChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="email"
                        name="email"
                        label="Email"
                        fullWidth
                        validators={['isEmail']}
                        errorMessages={['Enter a valid email address']}
                        value={data.email}
                        onChange={handleChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="addressLine1"
                        name="addressLine1"
                        label="Address Line 1"
                        fullWidth
                        required
                        validators={['required', 'minStringLength:1']}
                        errorMessages={['required field', "Field cannot be empty"]}
                        value={data?.address?.addressLine1}
                        onChange={handleAddressChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="addressLine2"
                        name="addressLine2"
                        label="Address Line 2"
                        fullWidth
                        required
                        validators={['required', 'minStringLength:1']}
                        errorMessages={['required field', "Field cannot be empty"]}
                        value={data?.address?.addressLine2}
                        onChange={handleAddressChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="city"
                        name="city"
                        label="Town/City"
                        fullWidth
                        required
                        validators={['required', 'minStringLength:1']}
                        errorMessages={['required field', "Field cannot be empty"]}
                        value={data?.address?.city}
                        onChange={handleAddressChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="eirCode"
                        name="eirCode"
                        label="EirCode/PostCode"
                        fullWidth
                        required
                        validators={['required', 'minStringLength:1']}
                        errorMessages={['required field', "Field cannot be empty"]}
                        value={data?.address?.eirCode}
                        onChange={handleAddressChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="country"
                        name="country"
                        label="Country"
                        fullWidth
                        required
                        validators={['required', 'minStringLength:1']}
                        errorMessages={['required field', "Field cannot be empty"]}
                        value={data?.address?.country}
                        onChange={handleAddressChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="website"
                        name="website"
                        label="Website"
                        fullWidth
                        value={data.website}
                        onChange={handleChange}
                    />
                </Grid2>

                <DataTypeAutoComplete
                    key="clientPriceBandId"
                    dataType={DataTypeEnum.PriceBands}
                    label="Select Price Band"
                    hasParent={false}
                    value={data.clientPriceBandId}
                    name={"clientPriceBandId"}
                    onChange={handleChange}
                    required={true}
                    fullWidth={true}
                />

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="vatNumber"
                        name="vatNumber"
                        label="VAT Number"
                        fullWidth
                        value={data.vatNumber}
                        onChange={handleChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!data.isSlaSent}
                                onChange={(e) =>
                                    setData((_data: any) => ({
                                        ..._data,
                                        isSlaSent: e.target.checked,
                                        slaSentDate: e.target.checked ? _data.slaSentDate : ''
                                    }))
                                }
                                name="isSlaSent"
                            />
                        }
                        label="SLA Sent"
                    />
                </Grid2>

                {data.isSlaSent && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <StandardTextValidator
                            key="slaSentDate"
                            name="slaSentDate"
                            label="SLA Sent Date"
                            type="date"
                            fullWidth
                            value={data.slaSentDate != null ? getFormatedDate(data.slaSentDate) : ""}
                            onChange={handleChange}
                        />
                    </Grid2>
                )}

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="xeroAccountNumber"
                        name="xeroAccountNumber"
                        label="Xero Account Number"
                        fullWidth
                        value={data.xeroAccountNumber}
                        onChange={handleChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        key="notes"
                        name="notes"
                        label="Notes"
                        fullWidth
                        multiline
                        rows={4}
                        value={data.notes}
                        onChange={handleChange}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <Grid2 container spacing={1}>
                        <Grid2 size={{ xs: data.id == null ? 12 : data?.isActive === false ? 12 : 6 }}>
                            <StandardButton
                                saving={saving}
                                loading={isLoading}
                                icon="save"
                                type="submit">
                                {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                            </StandardButton>
                        </Grid2>

                        {(!!id && data?.isActive === true) &&
                            <Grid2 size={{ xs: 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="block"
                                    variant="error"
                                    onClick={disable}>
                                    Disable
                                </StandardButton>
                            </Grid2>
                        }
                    </Grid2>
                </Grid2>
            </Grid2>

        </ValidatorForm>
    );
};